import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Building2, Star, MapPin, Phone, Globe, ArrowLeft, Loader2, TrendingUp, MessageCircle, Mail, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import SEO, { getLocalBusinessSchema, getBreadcrumbSchema } from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company logo with fallback on error
const CompanyLogoWithFallback = ({ logoUrl, name }) => {
  const [hasError, setHasError] = useState(false);
  
  if (!logoUrl || hasError) {
    return <Building2 className="w-12 h-12 text-slate-400" />;
  }
  
  return (
    <img 
      src={logoUrl} 
      alt={name} 
      className="w-24 h-24 object-cover"
      onError={() => setHasError(true)}
    />
  );
};

function StarDisplay({ count, filled }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <Star 
        key={i} 
        className={`w-5 h-5 ${i <= filled ? 'text-yellow-500 fill-yellow-500' : 'text-slate-300'}`} 
      />
    );
  }
  return <div className="flex gap-1">{stars}</div>;
}

function ReviewItem({ review, isOwner, onReply }) {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');

  const submitReply = () => {
    if (replyText.trim()) {
      onReply(review.review_id, replyText);
      setShowReply(false);
      setReplyText('');
    }
  };

  return (
    <div className="border-b pb-4 last:border-0">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <span className="font-medium">{review.user_name}</span>
          <StarDisplay filled={review.rating} />
        </div>
        <span className="text-xs text-slate-500">
          {new Date(review.created_at).toLocaleDateString('pl-PL')}
        </span>
      </div>
      <p className="text-sm text-slate-600 mb-3">{review.comment}</p>
      
      {review.reply || review.company_reply ? (
        <div className="ml-4 p-3 bg-orange-50 rounded-lg border-l-2 border-orange-500">
          <p className="text-xs text-orange-600 font-medium mb-1">Odpowiedź firmy:</p>
          <p className="text-sm">{review.reply || review.company_reply}</p>
        </div>
      ) : isOwner ? (
        showReply ? (
          <div className="mt-3 space-y-2">
            <Textarea 
              value={replyText} 
              onChange={(e) => setReplyText(e.target.value)} 
              rows={3} 
              placeholder="Napisz odpowiedź..."
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowReply(false)}>Anuluj</Button>
              <Button size="sm" onClick={submitReply} className="bg-orange-500">Wyślij</Button>
            </div>
          </div>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => setShowReply(true)} className="text-orange-600">
            <MessageCircle className="w-4 h-4 mr-1" />Odpowiedz
          </Button>
        )
      ) : null}
    </div>
  );
}

function ServiceBadge({ service }) {
  return <Badge variant="secondary">{service}</Badge>;
}

function GalleryImage({ src, index }) {
  const [hasError, setHasError] = useState(false);
  
  if (hasError || !src) {
    return (
      <div className="aspect-square rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
        <Building2 className="w-8 h-8 text-slate-300" />
      </div>
    );
  }
  
  return (
    <div className="aspect-square rounded-lg overflow-hidden">
      <img 
        src={src} 
        alt={`Praca ${index + 1}`} 
        className="w-full h-full object-cover"
        onError={() => setHasError(true)}
      />
    </div>
  );
}

export default function CompanyDetails() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useAuth();
  
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');

  const isOwner = user?.user_type === 'company' && company?.user_id === user?.user_id;
  const canReview = isAuthenticated && !isOwner; // Każdy zalogowany użytkownik może dodać opinię (oprócz właściciela firmy)

  async function fetchData() {
    try {
      const res = await fetch(`${API}/companies/${companyId}`);
      if (res.ok) setCompany(await res.json());
      else navigate('/companies');
    } catch (e) {
      navigate('/companies');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchData(); }, [companyId]);

  async function submitReview() {
    if (!comment.trim()) { toast.error('Komentarz wymagany'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ company_id: companyId, rating, comment }),
      });
      if (res.ok) {
        toast.success('Opinia dodana!');
        setShowForm(false);
        setComment('');
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Błąd');
      }
    } catch (e) { toast.error('Błąd'); }
    finally { setSubmitting(false); }
  }

  async function handleReply(reviewId, text) {
    try {
      const res = await fetch(`${API}/reviews/${reviewId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ reply: text }),
      });
      if (res.ok) {
        toast.success('Odpowiedź dodana');
        fetchData();
      }
    } catch (e) { toast.error('Błąd'); }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!company) return null;

  const reviews = company.reviews || [];
  const services = company.services || [];
  const gallery = company.gallery_images || [];
  const avgRating = company.rating || 0;

  // SEO data
  const companyName = company.company_name || company.name;
  const breadcrumbs = [
    { name: 'Strona główna', path: '/' },
    { name: 'Firmy', path: '/companies' },
    { name: companyName, path: `/companies/${company.user_id}` }
  ];
  
  const structuredData = [
    getLocalBusinessSchema(company),
    getBreadcrumbSchema(breadcrumbs)
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-details-page">
      <SEO 
        title={`${companyName} - Firma budowlana`}
        description={`${companyName}${company.location ? ` - ${company.location}` : ''}. ${services.slice(0, 3).join(', ')}. ${company.description?.substring(0, 100) || 'Sprawdź opinie i skontaktuj się z wykonawcą.'}...`}
        canonical={`/companies/${company.user_id}`}
        image={company.logo_url || undefined}
        type="profile"
        structuredData={structuredData}
      />
      
      <div className="bg-slate-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button variant="ghost" onClick={() => navigate('/companies')} className="text-slate-400 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />Powrót
          </Button>
          <div className="flex items-start gap-6">
            <div className="w-24 h-24 bg-slate-800 rounded-lg flex items-center justify-center overflow-hidden">
              <CompanyLogoWithFallback logoUrl={company.logo_url} name={company.name} />
            </div>
            <div>
              {company.is_promoted && (
                <Badge className="bg-orange-500 text-white mb-2">
                  <TrendingUp className="w-3 h-3 mr-1" />Promowana
                </Badge>
              )}
              <h1 className="font-heading font-bold text-2xl text-white">{company.name}</h1>
              {company.location && (
                <p className="text-slate-400 flex items-center gap-1 mt-1">
                  <MapPin className="w-4 h-4" />{company.location}
                </p>
              )}
              <div className="flex items-center gap-2 mt-3">
                <StarDisplay filled={Math.round(avgRating)} />
                <span className="font-bold text-white">{avgRating > 0 ? avgRating.toFixed(1) : '-'}</span>
                <span className="text-slate-400">({company.reviews_count || 0} opinii)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {company.description && (
              <Card>
                <CardHeader><CardTitle>O firmie</CardTitle></CardHeader>
                <CardContent><p className="text-slate-700 whitespace-pre-wrap">{company.description}</p></CardContent>
              </Card>
            )}

            {services.length > 0 && (
              <Card>
                <CardHeader><CardTitle>Usługi</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {services.map((s, i) => <ServiceBadge key={i} service={s} />)}
                  </div>
                </CardContent>
              </Card>
            )}

            {gallery.length > 0 && (
              <Card>
                <CardHeader><CardTitle>Galeria</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2">
                    {gallery.map((img, i) => <GalleryImage key={i} src={img} index={i} />)}
                  </div>
                </CardContent>
              </Card>
            )}

            {company.location && (
              <Card>
                <CardHeader><CardTitle>Lokalizacja</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-slate-700 mb-4">{company.location}</p>
                  <a 
                    href={`https://www.openstreetmap.org/search?query=${encodeURIComponent(company.location + ', Polska')}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-orange-600 hover:underline"
                  >
                    Zobacz na mapie →
                  </a>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Opinie ({reviews.length})</CardTitle>
                  {canReview && !showForm && (
                    <Button variant="outline" size="sm" onClick={() => setShowForm(true)}>
                      <Star className="w-4 h-4 mr-1" />Dodaj opinię
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {showForm && (
                  <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                    <div className="mb-4">
                      <Label className="mb-2 block">Twoja ocena</Label>
                      <div className="flex gap-2">
                        {[1,2,3,4,5].map(n => (
                          <button key={n} onClick={() => setRating(n)}>
                            <Star className={`w-8 h-8 ${n <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-slate-300'}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mb-4">
                      <Label>Komentarz</Label>
                      <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button variant="outline" onClick={() => setShowForm(false)}>Anuluj</Button>
                      <Button onClick={submitReview} disabled={submitting} className="bg-orange-500">
                        {submitting ? 'Dodawanie...' : 'Dodaj'}
                      </Button>
                    </div>
                  </div>
                )}

                {reviews.length > 0 ? (
                  <div className="space-y-4">
                    {reviews.map(r => (
                      <ReviewItem key={r.review_id} review={r} isOwner={isOwner} onReply={handleReply} />
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-6">Brak opinii</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold mb-4">Kontakt</h3>
                <div className="space-y-3">
                  {company.phone && (
                    <div className="flex items-center gap-2 text-slate-600" data-testid="company-phone">
                      <Phone className="w-4 h-4" />{company.phone}
                    </div>
                  )}
                  {company.email && (
                    <a href={`mailto:${company.email}`} className="flex items-center gap-2 text-slate-600 hover:text-orange-600">
                      <Mail className="w-4 h-4" />{company.email}
                    </a>
                  )}
                  {company.website && (
                    <a 
                      href={company.website.startsWith('http') ? company.website : `https://${company.website}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center gap-2 text-slate-600 hover:text-orange-600"
                      data-testid="company-website-link"
                    >
                      <Globe className="w-4 h-4" />Strona WWW
                    </a>
                  )}
                </div>
                
                {/* Przycisk wysyłania wiadomości - widoczny dla zalogowanych użytkowników (nie właścicieli firmy) */}
                {isAuthenticated && !isOwner && (
                  <div className="space-y-2 mt-4">
                    <Button 
                      onClick={() => navigate(`/chat?user=${company.user_id}`)}
                      className="w-full bg-orange-500 hover:bg-orange-600"
                      data-testid="send-message-btn"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Wyślij wiadomość
                    </Button>
                    <Button 
                      onClick={() => setShowForm(true)}
                      variant="outline"
                      className="w-full border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                      data-testid="add-review-btn"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Dodaj opinię
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <h3 className="font-bold mb-4">Ocena</h3>
                <p className="font-bold text-4xl">{avgRating > 0 ? avgRating.toFixed(1) : '-'}</p>
                <StarDisplay filled={Math.round(avgRating)} />
                <p className="text-sm text-slate-500 mt-1">{company.reviews_count || 0} opinii</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
